import { TENANT_TYPES, ENGAGEMENT_TYPES } from '../constants';

// Alert policy constants (matching backend enum exactly)
export const ALERT_POLICY = {
  OPTIMIZED: 'optimized', // Strict filtering, minimal false positives
  STRICT: 'strict',
  LIGHT: 'light',
  PROVISIONING_OPTIMIZED: 'provisioning_optimized',
  MANAGED_OPTIMIZED: 'managed_optimized' // More permissive, more alerts to review
};

// Array of all optimized policy variants
export const OPTIMIZED_POLICIES = [
  ALERT_POLICY.OPTIMIZED,
  ALERT_POLICY.MANAGED_OPTIMIZED,
  ALERT_POLICY.PROVISIONING_OPTIMIZED
];

// Policy configuration map matching backend logic in tenant-configurations.js
// Structure: { [tenantType]: { [engagementType]: { policy, selfService } } }
// Note: selfService=true → strict filtering (fewer alerts, less noise)
//       selfService=false → permissive filtering (more alerts for review)
const POLICY_MAP = {
  [TENANT_TYPES.CYBERINT_PROVISIONING]: {
    [ENGAGEMENT_TYPES.DEMO]: {
      policy: ALERT_POLICY.PROVISIONING_OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.POC]: {
      policy: ALERT_POLICY.MANAGED_OPTIMIZED,
      selfService: false
    },
    [ENGAGEMENT_TYPES.UNMANAGED_POC]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.SIZING]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.INTERNAL_USE]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.TEM_ESSENTIALS]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    }
  },

  [TENANT_TYPES.MSSP_PROVISIONING]: {
    [ENGAGEMENT_TYPES.DEMO]: {
      policy: ALERT_POLICY.PROVISIONING_OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.POC]: {
      policy: ALERT_POLICY.MANAGED_OPTIMIZED,
      selfService: false
    },
    [ENGAGEMENT_TYPES.UNMANAGED_POC]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.SIZING]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    }
  },

  [TENANT_TYPES.CYBERINT_CUSTOMER]: {
    [ENGAGEMENT_TYPES.ASM]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.BRONZE]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.SILVER]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.GOLD]: {
      policy: ALERT_POLICY.MANAGED_OPTIMIZED,
      selfService: false
    },
    [ENGAGEMENT_TYPES.PLATINUM]: {
      policy: ALERT_POLICY.MANAGED_OPTIMIZED,
      selfService: false
    },
    [ENGAGEMENT_TYPES.BRAND_ABUSE]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.DARKWEB]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    },
    [ENGAGEMENT_TYPES.TEM_ESSENTIALS]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    }
  },

  [TENANT_TYPES.MSSP_MANAGEMENT]: {
    [ENGAGEMENT_TYPES.MSSP_MGMT_UNMANAGED_POC]: {
      policy: ALERT_POLICY.OPTIMIZED,
      selfService: true
    }
  },

  // Tenant types with default managed policy (no engagement type variants)
  [TENANT_TYPES.MSSP_STANDALONE_ALLOCATION]: {},
  [TENANT_TYPES.MSSP_PARENT_ALLOCATION]: {},
  [TENANT_TYPES.MSSP_REPORT]: {},
  [TENANT_TYPES.CYBERINT_MANAGEMENT]: {}
};

/**
 * Maps tenant type and engagement type to alert configuration policy settings
 * Matches backend logic in tenant-mgmt-service/src/utils/tenant-configurations.js
 *
 * @param {string} tenantType - The tenant type (e.g., CYBERINT_PROVISIONING, CYBERINT_CUSTOMER, etc.)
 * @param {string} engagementType - The engagement type (e.g., DEMO, POC, BRONZE, etc.)
 * @returns {{
 *   policy: string,
 *   selfService: boolean
 * }} - Object containing:
 *   - policy: Alert policy preset name
 *   - selfService: true = strict filtering (fewer alerts), false = permissive (more alerts)
 */
export const getPolicyForEngagementType = (tenantType, engagementType) => {
  // Use optional chaining to handle undefined tenantType gracefully
  const mapping = POLICY_MAP[tenantType]?.[engagementType];

  if (mapping) {
    return mapping;
  }

  // No mapping found - fallback to managed optimized (matches backend behavior)
  // This is equivalent to backend's: selfService ? OPTIMIZED : MANAGED_OPTIMIZED
  // where selfService defaults to false when no mapping exists
  return {
    policy: ALERT_POLICY.MANAGED_OPTIMIZED,
    selfService: false
  };
};

/**
 * Checks if engagement type change should auto-update self-service toggle
 *
 * @param {string} tenantType - The tenant type
 * @param {string} engagementType - The engagement type
 * @returns {boolean} - True if self-service should be auto-managed
 */
export const shouldAutoManageSelfService = (tenantType, engagementType) => {
  if (!tenantType || !engagementType) return false;

  const { policy } = getPolicyForEngagementType(tenantType, engagementType);
  // Only auto-manage if we have a valid policy mapping
  return policy !== null;
};
